import { z } from 'zod';
import {
  PLANS_IN_ORDER,
  getPlan,
  planKeySchema,
  type PlanKey,
} from '../constants/plans.js';
import type { PlanRefusal, SubscriptionSnapshot } from './entitlements.js';

/**
 * METERING — how much of what the shop has bought is already spent.
 *
 * The seat allowance in `entitlements.ts` is the same idea and came first, but it is the only
 * limit this product ever counted, and it is counted at exactly one moment (inviting somebody).
 * That is not enough to sell a tier on. A shop cannot decide whether the next plan up is worth
 * ₹500 a month if the only number it is ever shown is a bill.
 *
 * Two decisions shape everything below.
 *
 * **A meter is shown where the resource lives, not only on the billing screen.** "Products
 * 12 of 50" belongs at the top of the price list, where somebody is about to add the
 * thirteenth. A number that is only on a screen the owner opens once a quarter changes nobody's
 * mind, and the refusal — when it comes — arrives as a surprise instead of as the end of a
 * countdown they had been watching.
 *
 * **Every allowance is generous enough that hitting one means the shop has grown**, not that
 * the product is being stingy. A limit that bites in week two teaches an owner that the tool
 * is against them. These numbers are set where a Coimbatore shop that has outgrown its tier
 * genuinely has.
 *
 * Reading is NEVER metered, and neither is deleting. A shop over its allowance after a
 * downgrade keeps every row it has — the same rule the module gate follows, for the same
 * reason: taking away data somebody already recorded is not a pricing lever, it is a loss.
 */

/* -------------------------------------------------------------------------------------------
 * What is counted
 * ---------------------------------------------------------------------------------------- */

/**
 * The resources a plan puts a ceiling on.
 *
 * Deliberately short. Every entry here is something a shop creates repeatedly and can picture
 * a number for; a meter on something nobody can count in their head ("47 of 200 audit rows")
 * is noise that teaches people to ignore meters.
 *
 * Staff seats are NOT here. They were metered first, by `seatUsage`, at a moment this file
 * knows nothing about — an invitation that has not been accepted is a spent seat, and folding
 * that rule into a generic counter would lose it.
 */
export const METERED_RESOURCES = ['customers', 'orders', 'products', 'stock_items', 'storage'] as const;
export type MeteredResource = (typeof METERED_RESOURCES)[number];
export const meteredResourceSchema = z.enum(METERED_RESOURCES);

/**
 * Over what stretch of time a resource is counted.
 *
 * `lifetime` is a stock — how many rows exist right now, so deleting one gives it back.
 * `monthly` is a flow — how many were created since the first of the month, so it resets on
 * its own and deleting one gives nothing back.
 *
 * Orders are the only flow, and that is the honest way to meter them: a shop's order book
 * grows forever, so a lifetime cap on orders would eventually refuse every shop that stayed.
 */
export const QUOTA_PERIODS = ['lifetime', 'monthly'] as const;
export type QuotaPeriod = (typeof QUOTA_PERIODS)[number];
export const quotaPeriodSchema = z.enum(QUOTA_PERIODS);

export interface MeteredResourceDefinition {
  readonly key: MeteredResource;
  readonly period: QuotaPeriod;
  /** Default English name; the UI resolves `usage.<key>.label` through i18next. */
  readonly defaultLabel: string;
  /**
   * True when the figure is a number of BYTES rather than a number of rows, which the UI has
   * to know before it can write "0.4 of 1 GB" instead of "429,496,729 of 1,073,741,824".
   */
  readonly bytes?: true;
}

export const METERED_RESOURCE_DEFINITIONS: Readonly<
  Record<MeteredResource, MeteredResourceDefinition>
> = Object.freeze({
  customers: { key: 'customers', period: 'lifetime', defaultLabel: 'Customers' },
  orders: { key: 'orders', period: 'monthly', defaultLabel: 'Orders this month' },
  products: { key: 'products', period: 'lifetime', defaultLabel: 'Catalogue items' },
  stock_items: { key: 'stock_items', period: 'lifetime', defaultLabel: 'Stock items' },
  storage: { key: 'storage', period: 'lifetime', defaultLabel: 'Photo storage', bytes: true },
});

export function meteredResourceDefinition(
  resource: MeteredResource,
): MeteredResourceDefinition {
  return METERED_RESOURCE_DEFINITIONS[resource];
}

/* -------------------------------------------------------------------------------------------
 * The allowances
 * ---------------------------------------------------------------------------------------- */

const GB = 1_073_741_824;

/** `null` is unlimited, exactly as it is for seats. */
export type QuotaAllowance = number | null;

/**
 * What each tier includes, per resource.
 *
 * Kept here rather than on `PlanDefinition` so that adding a metered resource is one edit to
 * one table instead of three edits to a definition each — and so this file, which is the only
 * thing that reads them, is also the only thing that has to be understood to change them.
 */
export const PLAN_QUOTAS: Readonly<Record<PlanKey, Readonly<Record<MeteredResource, QuotaAllowance>>>> =
  Object.freeze({
    starter: Object.freeze({
      customers: 500,
      orders: 100,
      products: 50,
      stock_items: 100,
      storage: 1 * GB,
    }),
    growth: Object.freeze({
      customers: 5_000,
      orders: 1_000,
      products: 500,
      stock_items: 1_000,
      storage: 10 * GB,
    }),
    /** The tier a shop moves to when it has stopped wanting to think about any of this. */
    pro: Object.freeze({
      customers: null,
      orders: null,
      products: null,
      stock_items: null,
      storage: null,
    }),
  });

export function quotaAllowance(planKey: PlanKey, resource: MeteredResource): QuotaAllowance {
  return PLAN_QUOTAS[planKey][resource];
}

/** The cheapest plan (above `current`) whose allowance for `resource` fits `needed`. */
export function nextPlanWithQuota(
  current: PlanKey,
  resource: MeteredResource,
  needed: number,
): PlanKey | null {
  const from = getPlan(current).sortOrder;
  const candidate = PLANS_IN_ORDER.find((plan) => {
    if (plan.sortOrder <= from) return false;
    const allowance = quotaAllowance(plan.key, resource);
    return allowance === null || allowance >= needed;
  });
  return candidate?.key ?? null;
}

/* -------------------------------------------------------------------------------------------
 * Usage
 * ---------------------------------------------------------------------------------------- */

export interface ResourceUsage {
  readonly resource: MeteredResource;
  readonly period: QuotaPeriod;
  readonly used: number;
  /** `null` means unlimited. */
  readonly allowance: QuotaAllowance;
  /** Left before the next one is refused. `null` when unlimited. */
  readonly remaining: number | null;
  /** 0–100, rounded, and `null` when unlimited — a bar with no end cannot be filled in. */
  readonly percentUsed: number | null;
  /** True when the next one would be refused. */
  readonly atLimit: boolean;
  /**
   * True when the shop is ALREADY past the line — only reachable by a downgrade, never by
   * creating something, because creating is what {@link atLimit} refuses.
   */
  readonly exceeded: boolean;
  /**
   * True once the meter is worth pointing at: four fifths spent, or over. What decides
   * whether the in-module affordance is a quiet grey line or a coloured one.
   */
  readonly nearLimit: boolean;
}

/** The fraction at which a meter stops being informational and starts being a warning. */
export const QUOTA_WARNING_FRACTION = 0.8;

export function resourceUsage(
  planKey: PlanKey,
  resource: MeteredResource,
  used: number,
): ResourceUsage {
  const allowance = quotaAllowance(planKey, resource);
  const period = meteredResourceDefinition(resource).period;
  const counted = Math.max(0, used);
  if (allowance === null) {
    return {
      resource,
      period,
      used: counted,
      allowance: null,
      remaining: null,
      percentUsed: null,
      atLimit: false,
      exceeded: false,
      nearLimit: false,
    };
  }
  return {
    resource,
    period,
    used: counted,
    allowance,
    remaining: Math.max(0, allowance - counted),
    percentUsed: Math.min(100, Math.round((counted / allowance) * 100)),
    atLimit: counted >= allowance,
    exceeded: counted > allowance,
    nearLimit: counted >= allowance * QUOTA_WARNING_FRACTION,
  };
}

/**
 * The same figure, for a write that would add more than one at a time — a bulk import of
 * four hundred customers against an allowance of five hundred with four hundred and ten
 * already in the book.
 *
 * Answered as "would this fit", not "are you at the line", because an import that is refused
 * halfway through has already written half a customer book somebody now has to unpick.
 */
export function quotaFits(
  planKey: PlanKey,
  resource: MeteredResource,
  used: number,
  adding: number,
): boolean {
  const allowance = quotaAllowance(planKey, resource);
  return allowance === null || used + adding <= allowance;
}

/* -------------------------------------------------------------------------------------------
 * Saying a figure out loud
 * ---------------------------------------------------------------------------------------- */

const KB = 1024;

/**
 * Bytes, in the units a shop owner thinks in.
 *
 * Deliberately coarse — one decimal place, and never more. "0.8 GB of 1 GB" is a sentence
 * somebody reads at a glance; "858,993,459 of 1,073,741,824" is one nobody reads at all, and a
 * storage meter is decoration on every screen it appears on until the day it is not.
 *
 * `reference` is what stops the two sides of one meter being said in two different units.
 * Left to itself, 820 MB used against a 1 GB allowance renders as "819.2 MB of 1.0 GB", which
 * asks the reader to divide by 1024 before they can tell whether they are nearly out. Passing
 * the allowance as the reference pins both halves to the same unit.
 */
export function formatBytes(bytes: number, reference = bytes): string {
  const unit = reference >= GB ? GB : reference >= KB * KB ? KB * KB : reference >= KB ? KB : 1;
  const suffix = unit === GB ? 'GB' : unit === KB * KB ? 'MB' : unit === KB ? 'KB' : 'B';
  if (unit === 1) return `${String(Math.max(0, Math.round(bytes)))} B`;
  return `${(bytes / unit).toFixed(1)} ${suffix}`;
}

/**
 * One side of a meter, written the way its resource is counted: a byte figure in gigabytes, a
 * row count as a plain number. Used by the refusal message and by the in-module affordance, so
 * the sentence an owner is refused with and the line they had been watching agree.
 */
export function formatQuotaAmount(
  resource: MeteredResource,
  value: number,
  reference = value,
): string {
  return meteredResourceDefinition(resource).bytes === true
    ? formatBytes(value, reference)
    : String(Math.max(0, Math.round(value)));
}

/* -------------------------------------------------------------------------------------------
 * The refusal
 * ---------------------------------------------------------------------------------------- */

function requiredPlanFields(
  key: PlanKey | null,
): Pick<PlanRefusal, 'requiredPlan' | 'requiredPlanLabel' | 'requiredPlanMonthlyPricePaise'> {
  if (!key) {
    return { requiredPlan: null, requiredPlanLabel: null, requiredPlanMonthlyPricePaise: null };
  }
  const plan = getPlan(key);
  return {
    requiredPlan: key,
    requiredPlanLabel: plan.defaultLabel,
    requiredPlanMonthlyPricePaise: plan.monthlyPricePaise,
  };
}

/**
 * "Starter includes 50 catalogue items and you have 50."
 *
 * The same {@link PlanRefusal} shape the seat and module refusals use, so a client that
 * already renders one upgrade prompt renders this one too without learning a second payload.
 * `limit` carries the resource key rather than a generic `quota`, because the sentence the
 * client writes is different for every one of them.
 */
export function quotaRefusal(
  snapshot: SubscriptionSnapshot,
  usage: ResourceUsage,
  adding = 1,
): PlanRefusal {
  return {
    code: 'plan_limit_reached',
    limit: usage.resource,
    currentPlan: snapshot.planKey,
    used: usage.used,
    ...(usage.allowance === null ? {} : { allowance: usage.allowance }),
    ...requiredPlanFields(
      nextPlanWithQuota(snapshot.planKey, usage.resource, usage.used + adding),
    ),
  };
}

/* -------------------------------------------------------------------------------------------
 * The wire
 * ---------------------------------------------------------------------------------------- */

export const resourceUsageSchema = z.object({
  resource: meteredResourceSchema,
  period: quotaPeriodSchema,
  used: z.number().int().nonnegative(),
  allowance: z.number().int().nonnegative().nullable(),
  remaining: z.number().int().nonnegative().nullable(),
  percentUsed: z.number().int().min(0).max(100).nullable(),
  atLimit: z.boolean(),
  exceeded: z.boolean(),
  nearLimit: z.boolean(),
});
export type ResourceUsageView = z.infer<typeof resourceUsageSchema>;

/**
 * Every meter at once, plus the plan they are measured against.
 *
 * One call, because the billing screen draws all of them and an in-module affordance is
 * cheap to serve from a response the client already has cached.
 */
export const resourceUsageReportSchema = z.object({
  planKey: planKeySchema,
  /** The first of the month the `monthly` meters are counting from, as an ISO date. */
  periodStart: z.string(),
  resources: z.array(resourceUsageSchema),
});
export type ResourceUsageReport = z.infer<typeof resourceUsageReportSchema>;

/** The first instant of the month a `monthly` meter is counting. */
export function monthlyPeriodStart(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
}
